import { Check } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import type {
  CandidateDecision,
  PendingDecisionAction,
} from '@/modules/candidate-detail/types';
import { RejectionMessageEditor } from '@/modules/candidates/components/rejection-message/RejectionMessageEditor';
import { useApproveCandidateJobMutation } from '@/modules/candidates/mutations/useApproveCandidateJobMutation';
import type { CandidateStatusApi } from '@/modules/candidates/types';
import { Button } from '@/modules/ui/components/button/Button';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { CandidateApprovalConfirm } from './CandidateApprovalConfirm';

interface CandidateDecisionAreaProps {
  decision: CandidateDecision;
  jobId: string;
  candidateId: string;
  firstName: string;
  /** Self-service jobs let the hiring manager approve a submitted candidate. */
  isSelfServiceJob: boolean;
  pendingAction: PendingDecisionAction;
  /** `rejectionMessage` is the edited text, or null to send the default one. */
  onReject: (rejectionMessage: string | null) => void;
  status: CandidateStatusApi | null;
}

const rejectableStatuses: CandidateStatusApi[] = [
  'New',
  'Shortlisted',
  'ContactStarted',
  'Contacted',
  'PrescreeningScheduled',
  'AiPrescreened',
  'Prescreened',
  'AssessmentCompleted',
  'ReadyForSubmission',
  'Submitted',
  'HiringManagerApproved',
  'Interviewing',
];

const terminalCopy: Partial<
  Record<
    CandidateDecision,
    { title: string; description: string; positive: boolean }
  >
> = {
  rejected: {
    title: 'Rejected.',
    description: 'This candidate is no longer being considered.',
    positive: false,
  },
  declined: {
    title: 'Declined.',
    description: 'The candidate has left the process.',
    positive: false,
  },
  hired: {
    title: 'Hired.',
    description: 'The hiring process is complete.',
    positive: true,
  },
};

/**
 * Candidate actions live in the detail header. Rejection asks for an inline
 * confirmation. On self-service jobs a submitted candidate can be approved
 * for a first-round interview, also confirmed inline. On agency jobs the
 * recruiter arranges interviews, so there is no positive action here.
 */
export function CandidateDecisionArea({
  decision,
  jobId,
  candidateId,
  firstName,
  isSelfServiceJob,
  pendingAction,
  onReject,
  status,
}: CandidateDecisionAreaProps) {
  const [confirming, setConfirming] = useState<'reject' | 'approve' | null>(
    null,
  );
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);
  // Outreach shows only the user's own threads, so only the sender can open it.
  const [approvedAsSender, setApprovedAsSender] = useState(false);
  const approveMutation = useApproveCandidateJobMutation();
  // The page stays mounted across the decision queue, so match the candidate.
  const approvedHere =
    approveMutation.isSuccess &&
    approveMutation.variables.jobId === jobId &&
    approveMutation.variables.candidateId === candidateId;
  // Show the approval before the brief refetch lands.
  const shownDecision =
    approvedHere && decision === 'awaiting' ? 'approved' : decision;
  const terminal = terminalCopy[shownDecision];
  const isBusy = pendingAction !== null || approveMutation.isPending;

  const stopConfirming = () => {
    setConfirming(null);
    setRejectionMessage(null);
  };

  const approve = (isSender: boolean) => {
    setApprovedAsSender(isSender);
    approveMutation.mutate(
      { jobId, candidateId },
      {
        // The decision area updating is the confirmation; only a failure toasts.
        onSuccess: stopConfirming,
        onError: (error) =>
          toast.error(actionErrorMessage('approve the candidate', error)),
      },
    );
  };

  if (terminal) {
    return (
      <p
        aria-live="polite"
        className="text-muted-foreground flex items-center gap-2 text-[13px]"
      >
        {terminal.positive ? (
          <Check className="text-semantic-560 size-4" />
        ) : (
          <span className="size-1.5 rounded-full bg-neutral-500" />
        )}
        <span>
          <span className="text-foreground font-medium">{terminal.title}</span>{' '}
          {terminal.description}
        </span>
      </p>
    );
  }

  if (confirming === 'approve') {
    return (
      <CandidateApprovalConfirm
        candidateId={candidateId}
        firstName={firstName}
        isApproving={approveMutation.isPending}
        jobId={jobId}
        onApprove={approve}
        onCancel={stopConfirming}
      />
    );
  }

  if (confirming === 'reject') {
    const isRejectionMessageEmpty =
      rejectionMessage !== null && rejectionMessage.trim() === '';

    return (
      <div className="animate-in fade-in flex w-full max-w-xl flex-col gap-3 duration-150 motion-reduce:animate-none">
        <RejectionMessageEditor
          jobId={jobId}
          candidateId={candidateId}
          firstName={firstName}
          message={rejectionMessage}
          onMessageChange={setRejectionMessage}
        />
        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-[13px]">
          <span>Reject {firstName}? This removes them from the process.</span>
          <Button
            autoFocus
            className="h-8 rounded-md"
            disabled={isRejectionMessageEmpty}
            loading={pendingAction === 'reject'}
            onClick={() => {
              onReject(rejectionMessage);
              stopConfirming();
            }}
            size="sm"
            variant="destructive"
          >
            Confirm reject
          </Button>
          <Button
            className="h-8 rounded-md"
            disabled={pendingAction !== null}
            onClick={stopConfirming}
            size="sm"
            variant="ghost"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const canReject = status !== null && rejectableStatuses.includes(status);
  const canApprove =
    isSelfServiceJob && status === 'Submitted' && !approvedHere;
  const messaged = approvedHere && approveMutation.data;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {shownDecision === 'approved' && (
        <p
          aria-live="polite"
          className="text-muted-foreground flex items-center gap-2 text-[13px]"
        >
          <Check className="text-semantic-560 size-4 shrink-0" />
          <span>
            <span className="text-foreground font-medium">Approved.</span>{' '}
            {messaged
              ? `Invitation sent to ${firstName} on LinkedIn. Arrange the next steps directly with them.`
              : `Arrange the first-round interview directly with ${firstName}.`}
          </span>
          {messaged && approvedAsSender && (
            <Link
              className="text-primary-700 font-medium hover:underline"
              to="/hm/messages"
            >
              Open outreach
            </Link>
          )}
        </p>
      )}
      {shownDecision === 'interviewing' && (
        <span className="text-muted-foreground text-[13px]">
          Interview in progress.
        </span>
      )}
      {canReject && (
        <Button
          className="h-8 rounded-md"
          disabled={isBusy}
          loading={pendingAction === 'reject'}
          onClick={() => setConfirming('reject')}
          size="sm"
          variant="destructive"
        >
          Reject
        </Button>
      )}
      {canApprove && (
        <Button
          className="h-8 rounded-md"
          disabled={isBusy}
          onClick={() => setConfirming('approve')}
          size="sm"
        >
          Approve for first round interview
        </Button>
      )}
    </div>
  );
}
